<template>
  <div id="mapa-industrial-dinamico-container" class="full-height">
    <!-- Header com Título e Seletor de Cliente -->
    <map-header
      :clientes="clientes"
      :cliente-selecionado="clienteSelecionado"
      :loading="loading.postos"
      :cliente-nome="selectedClientDetails && (selectedClientDetails.nomeFantasia || selectedClientDetails.name)"
      :cliente-info1="selectedClientDetails && selectedClientDetails.info1"
      :cliente-info2="selectedClientDetails && selectedClientDetails.info2"
      @cliente-change="onClienteChange"
    />

    <!-- Área Principal com Layout Horizontal -->
    <div class="map-main-area">
      <!-- Menu Lateral Flutuante -->
      <floating-side-menu
        v-model="activeTab"
        :submenus="submenus"
        class="side-menu-container"
        @coleta-click="onColetaClick"
        @posto-click="onPostoClick"
        @ponto-click="onPontoClick"
        @cacamba-click="onCacambaClick"
      />

      <!-- Área do Mapa -->
      <div class="map-content">
        <map-viewer-area
          :postos="postos"
          :clientes="clientes"
          :cliente-selecionado="clienteSelecionado"
          :filtros-ativos="filtrosAtivos"
          :loading="loading.map"
          @posto-click="onPostoClick"
          @cliente-change="onClienteChange"
        />
      </div>
    </div>

    <!-- Modais -->
    <modal-detalhes-coleta
      v-if="modals.detalheColeta.isOpen"
      :coleta-id="modals.detalheColeta.taskId"
      @close="closeModal('detalheColeta')"
    />
    <modal-detalhes-posto
      v-if="modals.detalhePosto.isOpen"
      :posto-id="modals.detalhePosto.collectionPointId"
      @close="closeModal('detalhePosto')"
    />

    <modal-pontos-coleta
      v-if="modals.pontosColeta.isOpen"
      :cliente-id="modals.pontosColeta.clienteId"
      @close="closeModal('pontosColeta')"
    />

    <modal-cacamba-temporaria
      v-if="modals.cacambaTemporaria.isOpen"
      :cacamba-id="modals.cacambaTemporaria.collectionPointId"
      @close="closeModal('cacambaTemporaria')"
    />
  </div>
</template>

<script>
import { Component, Vue, Watch } from 'vue-property-decorator'
import { IndustrialMapRepository } from 'src/modules/industry/industrial-map/IndustrialMapRepository.js'
import MapHeader from 'src/components/industrialMap/MapHeader.vue'
import FloatingSideMenu from 'src/components/industrialMap/FloatingSideMenu.vue'
import MapViewerArea from 'src/components/industrialMap/MapViewerArea.vue'
import ModalDetalhesColeta from 'src/components/industrialMap/modals/ModalDetalhesColeta.vue'
import ModalCacambaTemporaria from 'src/components/industrialMap/modals/ModalCacambaTemporaria.vue'

@Component({
  name: 'MapaIndustrialDinamico',
  components: {
    MapHeader,
    FloatingSideMenu,
    MapViewerArea,
    ModalDetalhesColeta,
    ModalCacambaTemporaria
  }
})
class MapaIndustrialDinamico extends Vue {
  clientes = []
  postos = [] // Declaração da propriedade postos
  clienteSelecionado = null
  selectedClientDetails = null

  activeTab = []
  filtrosAtivos = {
    coletas: true,
    rondas: true,
    postos: true,
    pontos: true,
    cacambas: true,
    residuos: false
  }

  resumo = {
    totalColetas: 0,
    totalRondas: 0,
    totalPostos: 0,
    totalCacambas: 0
  }

  submenus = {
    coletas: [],
    rondas: [],
    postosAvancados: [],
    pontosColeta: [],
    cacambas: [],
    topResiduos: []
  }

  modals = {
    detalheColeta: { isOpen: false, taskId: null },
    detalhePosto: { isOpen: false, collectionPointId: null },
    pontosColeta: { isOpen: false, clienteId: null },
    cacambaTemporaria: { isOpen: false, collectionPointId: null }
  }

  loading = {
    postos: false,
    submenus: false,
    detalhes: false,
    map: false
  }

  async mounted () {
    await this.loadClientes()
    await this.loadMapData(this.clienteSelecionado)
  }

  async loadClientes () {
    try {
      this.loading.postos = true

      const { registros } = await IndustrialMapRepository.findClients()

      this.clientes = registros.map(cliente => ({
        id: cliente.id,
        name: cliente.name || cliente.fantasyName,
        nomeFantasia: cliente.fantasyName || cliente.name,
        info1: null,
        info2: null
      }))
    } catch (error) {
      this.clientes = []
    } finally {
      this.loading.postos = false
    }
  }

  extractPostoNumber (name) {
    const match = name.match(/PA\s*(\d{2})/)
    if (match) {
      return parseInt(match[1], 10)
    }
    return null
  }

  // Método unificado para carregar dados (visão geral ou cliente específico)
  async loadMapData (clientId = null) {
    this.loading.postos = true
    this.loading.submenus = true
    this.loading.detalhes = true

    try {
      // Fazer requisição com clientId (null para visão geral, ID específico para cliente)
      const industrialMapResponse = await IndustrialMapRepository.mountIndustrialMap(null, null, clientId, '20750003,20750006,20750007')

      // Mapear e ordenar postos avançados
      this.postos = (industrialMapResponse?.collectionPointsOutpost || [])
        .map(posto => {
          const numero = this.extractPostoNumber(posto.name)

          return {
            id: posto.id,
            name: posto.name,
            code: posto.areaCode,
            clientId: posto.clientId,
            latitude: posto.latitude,
            longitude: posto.longitude,
            number: posto.number,
            numero: numero,
            criticality: posto.criticalityStatus || 'green',
            totalCapacity: posto.totalCapacity,
            totalUsedKg: posto.totalUsedKg,
            packingCount: posto.packingCount,
            usedPercentage: posto.usedPercentage
          }
        })
        .sort((a, b) => {
          if (a.numero !== null && b.numero !== null) {
            return a.numero - b.numero
          }
          if (a.numero === null) return 1
          if (b.numero === null) return -1
          return 0
        })

      // Atualizar submenus
      this.submenus.coletas = (industrialMapResponse?.collections || []).map(coleta => ({
        ...coleta,
        id: coleta.collectionPointId || coleta.id
      }))

      this.submenus.postosAvancados = this.postos || []
      this.submenus.cacambas = industrialMapResponse?.temporaryDumpsters || []
      this.submenus.pontosColeta = industrialMapResponse?.collections || []
      this.submenus.rondas = industrialMapResponse?.rounds || []
      this.submenus.topResiduos = industrialMapResponse?.topFiverResidues || []

      // Atualizar resumo
      this.resumo = {
        totalColetas: this.submenus.coletas.length,
        totalRondas: this.submenus.rondas.length,
        totalPostos: this.postos.length,
        totalCacambas: this.submenus.cacambas.length
      }
    } catch (error) {
      console.error('loadMapData', error)
      this.postos = []
      this.submenus = { coletas: [], rondas: [], postosAvancados: [], pontosColeta: [], cacambas: [], topResiduos: [] }
      this.resumo = { totalColetas: 0, totalRondas: 0, totalPostos: 0, totalCacambas: 0 }
    } finally {
      this.loading.postos = false
      this.loading.submenus = false
      this.loading.detalhes = false
    }
  }

  get clienteCompleto () {
    return this.clientes.find(c => c.id === this.clienteSelecionado) || null
  }

  @Watch('clienteSelecionado')
  async onClienteSelecionadoChange (newClienteId) {
    await this.loadMapData(newClienteId)
  }

  async onPostoClick (posto) {
    this.modals.detalhePosto = {
      isOpen: true,
      collectionPointId: posto.id
    }
  }

  onClienteChange (clienteId) {
    this.clienteSelecionado = clienteId
    this.selectedClientDetails = this.clientes.find(c => c.id === clienteId) || null
  }

  onColetaClick (coleta) {
    this.modals.detalheColeta = {
      isOpen: true,
      taskId: coleta.id
    }
  }

  onPontoClick (ponto) {
    if (ponto && ponto.openModal) {
      this.modals.pontosColeta = {
        isOpen: true,
        clienteId: this.clienteSelecionado
      }
    } else {
      this.modals.pontosColeta = {
        isOpen: true,
        clienteId: this.clienteSelecionado
      }
    }
  }

  onCacambaClick (cacamba) {
    this.modals.cacambaTemporaria = {
      isOpen: true,
      collectionPointId: cacamba.id
    }
  }

  closeModal (modalName) {
    if (this.modals[modalName]) {
      this.modals[modalName].isOpen = false
      this.modals[modalName].taskId = null
      this.modals[modalName].collectionPointId = null
      this.modals[modalName].clienteId = null
    }
  }
}
export default MapaIndustrialDinamico
</script>

<style lang="stylus" scoped>
@import '~quasar-variables'

#mapa-industrial-dinamico-container
  display: flex
  flex-direction: column
  height: 100vh
  width: 100vw
  overflow: hidden
  background-color: #fff

  .map-main-area
    display: flex
    flex: 1
    background: #B5D78D
    overflow: hidden
    min-height: 0
    margin-left: 20px
    margin-right: 20px
    margin-bottom: 20px

    .side-menu-container
      width: 320px
      min-width: 320px
      max-width: 320px
      height: 100%
      z-index: 100
      background: transparent

    .map-content
      flex: 1
      height: 100%
      min-width: 0
      overflow: auto

@media (max-width: 992px)
  #mapa-industrial-dinamico-container
    .map-main-area
      .side-menu-container
        width: 280px
        min-width: 280px

@media (max-width: 768px)
  #mapa-industrial-dinamico-container
    .map-main-area
      flex-direction: column

      .side-menu-container
        width: 100%
        min-width: 100%
        order: 2

      .map-content
        order: 1
        height: 60vh
</style>
